import { gql } from "@apollo/client";
import classNames from "classnames";
import { useContext, useMemo, useState } from "react";
import useCuratedSearchDesignStateMachine from "../../../reactHooks/curatedSearch/useCuratedSearchDesignStateMachine";
import {
  AutopilotSearchStrategyJobEnum,
  PreviousResultsInput,
} from "../../../__generated__/graphql";
import {
  FunnelIcon,
  SparklesIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import CuratedSearchDesignContactsPreviewTable, {
  CuratedSearchDesignContactsPreviewTable_OrgContactInternalSearchPreview,
  CuratedSearchDesignContactsPreviewTable_SearchAnalysisPreview,
} from "./CuratedSearchDesignContactsPreviewTable";
import {
  CuratedSearchDesignTabStatusEnum,
  getStateDescription,
} from "./CuratedSearchDesignTabsContainer";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import CuratedSearchDesignPowerFilterContainer from "./CuratedSearchDesignPowerFilterContainer";
import CuratedSearchDesignEditCriteriaModal from "./CuratedSearchDesignEditCriteriaModal";
import pluralize from "pluralize";
import CuratedSearchDesignPostAnalysisActions, {
  CuratedSearchDesignPostAnalysisActions_SearchAnalysisPreview,
} from "./CuratedSearchDesignPostAnalysisActions";
import CiroFadingText from "../../shared/CiroFadingText";
import { ICuratedSearchDesignTabStatus } from "./CuratedSearchDesignTab";
import { PreviewContactFeedbackMap } from "../../../contexts/InternalAutopilotHydrationContext";
import CiroSpinner from "../../shared/CiroSpinner";
import approx from "approximate-number";

const MAX_ELASTICSEARCH_RESULT_COUNT = 10000;

export const CuratedSearchDesignTabContainer_CreateInternalAutopilotFilters = gql`
  mutation CuratedSearchDesignTabContainer_CreateInternalAutopilotFilters(
    $input: CreateInternalAutopilotFiltersInput!
  ) {
    createInternalAutopilotFilters(input: $input) {
      id
      internalCuratedSearch {
        auxiliary_filters {
          companyNames
          orgCompanyIds
        }
        opensearchFiltersBlob
      }
    }
  }
`;

export const CuratedSearchDesignTabContainer_OrgContactInternalSearchPreview = gql`
  mutation CuratedSearchDesignTabContainer_OrgContactInternalSearchPreview(
    $input: OrgContactInternalSearchPreviewInput!
  ) {
    orgContactInternalSearchPreview(input: $input) {
      orgContacts {
        id
      }
      totalResultCount
      ...CuratedSearchDesignContactsPreviewTable_OrgContactInternalSearchPreview
    }
  }

  ${CuratedSearchDesignContactsPreviewTable_OrgContactInternalSearchPreview}
`;

export const CuratedSearchDesignTabContainer_SearchAnalysisPreview = gql`
  mutation CuratedSearchDesignTabContainer_SearchAnalysisPreview(
    $input: SearchAnalysisPreviewInput!
  ) {
    searchAnalysisPreview(input: $input) {
      analysisResults {
        org_contact {
          id
        }
        scoringCriteriaResults {
          criterion_fit
          reasoning
          criterion_id
          sources
        }
      }
      ...CuratedSearchDesignContactsPreviewTable_SearchAnalysisPreview
      ...CuratedSearchDesignPostAnalysisActions_SearchAnalysisPreview
    }
  }

  ${CuratedSearchDesignContactsPreviewTable_SearchAnalysisPreview}
  ${CuratedSearchDesignPostAnalysisActions_SearchAnalysisPreview}
`;

export const CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearch = gql`
  mutation CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearch(
    $input: UpsertAutopilotCuratedSearchInput!
  ) {
    upsertAutopilotCuratedSearch(input: $input) {
      success
      autopilotCuratedSearch {
        id
      }
    }
  }
`;

const CuratedSearchDesignTabContainer = ({
  strategy,
  prompt,
  setTabStatus,
  tabStatus,
}: {
  strategy: AutopilotSearchStrategyJobEnum;
  prompt: string;
  tabStatus: ICuratedSearchDesignTabStatus;
  setTabStatus: (tabStatus: ICuratedSearchDesignTabStatus) => void;
}) => {
  const { scoringCriteriaValues } = useContext(AutopilotCuratedSearchContext);

  const [showFilters, setShowFilters] = useState(false);
  const [remixPrompt, setRemixPrompt] = useState<string | null>(null);

  const [previewContactFeedback, setPreviewContactFeedback] =
    useState<PreviewContactFeedbackMap>(
      new Map<number, PreviousResultsInput>(),
    );

  // Use the state machine hook
  const { filters, previews, searchAnalysis, curatedSearchId } =
    useCuratedSearchDesignStateMachine({
      strategy,
      prompt,
      tabStatus,
      setTabStatus,
      scoringCriteriaValues,
      setPreviewContactFeedback,
      remixContext: remixPrompt
        ? {
            remixPrompt,
            previousInternalSearchFilters: {
              opensearchFiltersBlob:
                tabStatus.savedFilters?.opensearchFiltersBlob || "",
              auxiliary_filters: {
                companyNames:
                  tabStatus.savedFilters?.auxiliary_filters?.companyNames || [],
                orgCompanyIds:
                  tabStatus.savedFilters?.auxiliary_filters?.orgCompanyIds ||
                  [],
              },
              id: tabStatus.savedFilters?.id,
            },
            previousResults: Array.from(previewContactFeedback.values()),
          }
        : null,
    });

  const approximateTotalResultCount = useMemo(() => {
    return approx(previews?.totalResultCount ?? 0);
  }, [previews]);

  return (
    <div>
      <div>
        <div className={classNames("p-4", "rounded-lg", "bg-gray-100")}>
          <div>
            <div
              className={classNames("flex", "justify-between", "items-start")}
            >
              <div className={classNames("flex", "flex-col")}>
                <div
                  className={classNames(
                    "flex",
                    "flex-row",
                    "items-center",
                    "gap-2",
                  )}
                >
                  <CiroFadingText
                    className={classNames("text-gray-700", "font-medium")}
                    text={getStateDescription(tabStatus.state, remixPrompt)}
                    fadeWindow={500}
                  />
                  {tabStatus.state !==
                    CuratedSearchDesignTabStatusEnum.COMPLETED && (
                    <div className={classNames("mt-[3px]")}>
                      <CiroSpinner loading={true} useBeatLoader={true} />
                    </div>
                  )}
                </div>
                {previews && previews.totalResultCount > 0 && (
                  <div
                    className={classNames("text-sm", "text-gray-500", "mt-1")}
                  >
                    {strategy ===
                    AutopilotSearchStrategyJobEnum.SpecificCompaniesStrategy
                      ? "Estimated "
                      : ""}
                    {approximateTotalResultCount}
                    {previews.totalResultCount ===
                    MAX_ELASTICSEARCH_RESULT_COUNT
                      ? "+"
                      : ""}{" "}
                    {pluralize("contact", previews.totalResultCount)}
                    {" eligible based on filters"}
                  </div>
                )}
              </div>
              {tabStatus.state >
                CuratedSearchDesignTabStatusEnum.LOADING_FILTERS && (
                <CiroButton
                  analyticsField="view filters for strategy"
                  analyticsProps={{
                    strategy,
                  }}
                  style={
                    showFilters
                      ? CiroButtonStyleEnum.BORDERLESS
                      : CiroButtonStyleEnum.PRIMARY
                  }
                  onClick={() => setShowFilters(!showFilters)}
                  customClassName={
                    showFilters
                      ? classNames(
                          "text-sm",
                          "text-gray-500",
                          "hover:text-gray-700",
                          "flex",
                          "items-center",
                        )
                      : undefined
                  }
                >
                  <div
                    className={classNames(
                      "flex",
                      "items-center",
                      "text-sm text-gray-500",
                    )}
                  >
                    {showFilters ? (
                      <ChevronUpIcon
                        className={classNames("w-4", "h-4", "mr-2")}
                      />
                    ) : (
                      <FunnelIcon
                        className={classNames("w-4", "h-4", "mr-2")}
                      />
                    )}
                    {showFilters ? "Hide filters" : "Show filters"}
                  </div>
                </CiroButton>
              )}
            </div>
          </div>
          {showFilters && (
            <CuratedSearchDesignPowerFilterContainer
              filters={filters}
              setShowFilters={setShowFilters}
              setFilters={(newFilters) => {
                setRemixPrompt(null);
                setTabStatus({
                  state: CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS,
                  savedFilters: newFilters,
                });
                setShowFilters(false);
              }}
            />
          )}
        </div>
      </div>
      {tabStatus.state > CuratedSearchDesignTabStatusEnum.LOADING_FILTERS && (
        <div className={classNames("pt-4")}>
          <div
            className={classNames(
              "flex",
              "justify-between",
              "items-center",
              "py-4",
              "px-1",
            )}
          >
            <div className={classNames("flex", "items-center", "gap-2")}>
              <div>
                <SparklesIcon
                  className={classNames("text-orange-500", "h-6")}
                />
              </div>
              <div
                className={classNames(
                  "text-md",
                  "font-semibold",
                  "text-gray-700",
                )}
              >
                Preview results
              </div>
            </div>
            <CuratedSearchDesignEditCriteriaModal />
          </div>
          <CuratedSearchDesignContactsPreviewTable
            contactsPreview={previews}
            contactsAnalysisPreview={searchAnalysis}
            previewContactFeedback={previewContactFeedback}
            setPreviewContactFeedback={setPreviewContactFeedback}
            criteriaLoading={
              tabStatus.state ===
              CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS
            }
          />
        </div>
      )}
      {tabStatus.state > CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS &&
        curatedSearchId &&
        filters &&
        searchAnalysis && (
          <div
            className={classNames(
              "flex",
              "flex-col",
              "gap-4",
              "w-full",
              "pt-4",
            )}
          >
            <CuratedSearchDesignPostAnalysisActions
              totalContacts={previews?.totalResultCount ?? 0}
              filters={filters}
              curatedSearchId={curatedSearchId}
              onRemix={(v: string) => {
                setRemixPrompt(v);
                setTabStatus({
                  state: CuratedSearchDesignTabStatusEnum.LOADING_FILTERS,
                  savedFilters: filters,
                });
              }}
              previewContactFeedback={previewContactFeedback}
              filtersAreOpen={showFilters}
            />
          </div>
        )}
    </div>
  );
};

export default CuratedSearchDesignTabContainer;
